'use client';

import { useMutation } from '@tanstack/react-query';
import { REGEXP_ONLY_DIGITS } from 'input-otp';

import { useEffect, useRef, useState } from 'react';

import { TLoginEmailSteps } from '@/components/auth-section/auth-section.types';
import AppLoader from '@/components/ui/app-loader';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';

import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { generateOTP, login } from '@/shared/services/auth';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { normalizeEmail } from '@/shared/utils/validation';

import { getApiErrorDetails } from '@/utils/axios';
import {
	ILoginEmailFormState,
	ILoginOTPFormState,
	loginEmailSchema,
	loginOTPSchema,
} from '@/utils/schema';

const OTP_LENGTH = 6;
interface ILoginModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onLoginSuccess: () => void;
}

function LoginModal({ open, onOpenChange, onLoginSuccess }: ILoginModalProps) {
	const otpInputRef = useRef<null | HTMLInputElement>(null);
	const [loginEmailStep, setLoginEmailStep] = useState<TLoginEmailSteps>('EMAIL');
	const [emailFormState, setEmailFormState] = useState<ILoginEmailFormState>({
		email: '',
		validator: loginEmailSchema,
		error: '',
	});

	const [otpFormState, setOtpFormState] = useState<ILoginOTPFormState>({
		otp: '',
		validator: loginOTPSchema,
		error: '',
	});

	// useMutation for OTP generation
	const generateOTPMutation = useMutation({
		mutationFn: generateOTP,
		retry: undefined,
		onSuccess: () => {
			setLoginEmailStep('OTP');
			emitMixpanelEvent(mixpanelEvents.VIEWED_LOGIN_PAGE_CODE_MODAL, {
				feature: MIXPANEL_APP_FEATURES.FORM,
			});
		},
		onError: (error) => {
			const { errorMessage, errorStatusCode } = getApiErrorDetails(error);
			let displayError = errorMessage;

			if (errorStatusCode === 500) {
				const normalizedMessage = errorMessage.trimEnd().endsWith('.')
					? errorMessage.trimEnd()
					: `${errorMessage.trimEnd()}.`;
				displayError = `${normalizedMessage} Please re-check the email and try again.`;
			} else if (errorStatusCode === 422) {
				displayError = 'This email could not be found. Please re-check the email and try again.';
			}

			setEmailFormState((prev) => ({
				...prev,
				error: displayError,
			}));
		},
	});

	// useMutation for login
	const loginMutation = useMutation({
		mutationFn: login,
		onSuccess: () => {
			onLoginSuccess();
		},
		onError: (error) => {
			const { errorMessage, errorStatusCode } = getApiErrorDetails(error);

			if (errorStatusCode === 403) {
				setOtpFormState((prevState) => ({
					...prevState,
					error: 'Too many attempts!',
				}));
			} else {
				setOtpFormState((prevState) => ({
					...prevState,
					error: errorMessage,
				}));
			}
		},
		retry: undefined,
	});

	const isLoading = generateOTPMutation.isPending || loginMutation.isPending;

	const modalHeader =
		loginEmailStep === 'EMAIL' ? 'Enter your email to sign-in' : 'OTP Verification';
	const modalDescription =
		loginEmailStep === 'EMAIL'
			? 'We will send you a verification code.'
			: `OTP sent to ${emailFormState.email}`;

	const handleGenerateOTP = (email: string) => {
		generateOTPMutation.mutate({
			email,
		});
	};

	const validateAndCallGenerateOTP = () => {
		const validationResult = loginEmailSchema.safeParse({
			email: emailFormState.email,
		});

		if (validationResult.error) {
			const fieldErrors = validationResult.error.flatten().fieldErrors;
			setEmailFormState((prev) => ({
				...prev,
				error: fieldErrors.email?.[0] ?? '',
			}));
			return;
		}

		handleGenerateOTP(validationResult.data.email);
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (isLoading) return;

		validateAndCallGenerateOTP();
	};

	const handleOnChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (isLoading) return;

		const value = e.target.value;

		setEmailFormState((prev) => ({
			...prev,
			email: value,
			error: '',
		}));
	};

	const handleInputEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (isLoading) return;
		if (e.key === 'Enter') {
			e.preventDefault();
			validateAndCallGenerateOTP();
		}
	};

	const validateAndCallLogin = (otp: string) => {
		const validationResult = loginOTPSchema.safeParse({ otp });

		if (validationResult.error) {
			const fieldErrors = validationResult.error.flatten().fieldErrors;
			setOtpFormState((prevState) => ({
				...prevState,
				error: fieldErrors.otp?.[0] ?? '',
			}));
			return;
		}

		loginMutation.mutate({
			login_type: 'EMAIL',
			token_id: null,
			otp,
			email: normalizeEmail(emailFormState.email),
		});
	};

	const handleSubmitLogin = (e: React.FormEvent) => {
		e.preventDefault();
		if (isLoading) return;

		validateAndCallLogin(otpFormState.otp);
	};

	const handleOtpInputChange = (otp: string) => {
		if (isLoading) return;

		if (otp.length === OTP_LENGTH) {
			validateAndCallLogin(otp);
		}

		setOtpFormState((prevState) => ({
			...prevState,
			otp,
			error: '',
		}));
	};

	const handleOtpInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (isLoading) return;

		if (e.key === 'Enter') {
			e.preventDefault();
			validateAndCallLogin(otpFormState.otp);
		}
	};

	const handleOpenChange = (open: boolean) => {
		if (isLoading) return;
		onOpenChange(open);
	};

	useEffect(() => {
		if (loginEmailStep === 'OTP') otpInputRef?.current?.focus();
	}, [loginEmailStep]);

	return (
		<Dialog
			open={open}
			onOpenChange={handleOpenChange}
		>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle className="text-2xl">{modalHeader}</DialogTitle>
				</DialogHeader>
				<DialogDescription>{modalDescription}</DialogDescription>
				{loginEmailStep === 'EMAIL' && (
					<form
						onSubmit={handleSubmit}
						className="space-y-4"
					>
						<div className="space-y-2">
							{/* <Label htmlFor="email">Email</Label> */}
							<Input
								id="email"
								name="email"
								placeholder="you@email.com"
								value={emailFormState.email}
								onChange={handleOnChangeEmail}
								onKeyDown={handleInputEmailKeyDown}
							/>
							{emailFormState.error && (
								<p className="text-sm font-light text-red-500">{emailFormState.error}</p>
							)}
						</div>
						<Button
							type="submit"
							className="mt-4 h-auto w-full py-3"
							disabled={isLoading}
						>
							Continue
							{isLoading && <AppLoader className="size-5" />}
						</Button>
					</form>
				)}
				{loginEmailStep === 'OTP' && (
					<form
						onSubmit={handleSubmitLogin}
						className="space-y-6"
					>
						<div className="justify-center space-y-2">
							<Label
								htmlFor="otp-input-0"
								className="sr-only"
							>
								OTP Input
							</Label>
							<InputOTP
								ref={otpInputRef}
								maxLength={OTP_LENGTH}
								value={otpFormState.otp}
								onChange={handleOtpInputChange}
								pattern={REGEXP_ONLY_DIGITS}
								onKeyDown={handleOtpInputKeyDown}
								className="w-full"
							>
								<InputOTPGroup className="flex-1 justify-between">
									<InputOTPSlot index={0} />
									<InputOTPSlot index={1} />
									<InputOTPSlot index={2} />
									<InputOTPSlot index={3} />
									<InputOTPSlot index={4} />
									<InputOTPSlot index={5} />
								</InputOTPGroup>
							</InputOTP>
							{otpFormState.error && (
								<p className="text-start text-sm text-red-500">{otpFormState.error}</p>
							)}
						</div>
						<Button
							type="submit"
							className="w-full"
							disabled={isLoading}
						>
							{isLoading ? 'Submitting...' : 'Submit'}
						</Button>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}

export default LoginModal;
